import moment from 'moment'
import qs from 'qs'

import {
  assertUnreachable,
  experiencePageUrlWithPrefilledState,
  getCruiseV2OfferUrl,
  offerPageUrlWithPrefilledState,
} from '..'

import {
  ValidBedbankCheckoutSchema,
  ValidExperienceCheckoutSchema,
  ValidHotelInstantBookingCheckoutSchema,
} from 'checkout/lib/utils/misc/restoreCartUtils'
import { RestoreCartProductType } from 'components/Pages/BookingRestoreCart/restoreCartUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { DEFAULT_ADULT_OCCUPANTS } from 'constants/flight'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import {
  buildStandaloneFlightSearchUrl as buildStandaloneFlightSearchUrlFromQueryParams,
  isOneWayFlight,
} from 'lib/flights/flightUtils'
import switchFunc from 'lib/function/switchFunc'
import offerPageURL, { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import {
  CAN_RESTORE_CART_BEDBANK_TO_NEW_CHECKOUT,
  CAN_RESTORE_CART_LE_HOTELS_TO_NEW_CHECKOUT,
} from 'tripPlanner/config'
import { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api'
import {
  FlightItem,
  HotelItem,
  LETripItem,
  SavedCruiseData,
  SavedExperienceData,
  SavedFlightSearchData,
  SavedHotelData,
  SavedTourData,
  TourItem,
} from 'tripPlanner/types/tripItem'

const getRestoreCartProductType = switchFunc<
  RestoreCartProductType,
  App.OfferType
>(
  {
    [OFFER_TYPE_BED_BANK]: 'bedbankOffer',
  },
  'leOffer',
)

export function buildRestoreCartURLWithQueryParams(
  item: LETripItem,
  offer?: OfferTypeForTripItemTypes,
  experience?: App.ExperienceOffer,
  curatedTripId?: string,
  params?: { [key: string]: any },
): string | undefined {
  const url = buildRestoreCartURL(item, offer, experience, curatedTripId)
  if (params && url) {
    return addQueryParamsToPath(url, params)
  } else {
    return url
  }
}

export function flightHasBundledHotel(
  data: SavedFlightSearchData,
): data is Utils.RequiredProp<SavedFlightSearchData, 'hotelBundle'> {
  return !!data.hotelBundle
}

export function flightHasBundledTour(
  data: SavedFlightSearchData,
): data is Utils.RequiredProp<SavedFlightSearchData, 'tourBundle'> {
  return !!data.tourBundle
}

export function hasBundledFlight<Data extends SavedHotelData | SavedTourData>(
  data: Data,
): data is Utils.RequiredProp<Data, 'flightBundle'> {
  return !!data.flightBundle
}

function buildRestoreCartURL(
  item: LETripItem,
  offer?: OfferTypeForTripItemTypes,
  experience?: App.ExperienceOffer,
  curatedTripId?: string,
): string | undefined {
  const { type, savedItemData } = item
  switch (type) {
    case 'HOTEL': {
      if (isBookableHotel(savedItemData)) {
        return getHotelRestoreCartURL(savedItemData, curatedTripId)
      } else {
        if (offer) {
          return offerPageUrlWithPrefilledState(offer, item, {
            scrollToOptions: true,
          })
        }
      }
      break
    }
    case 'FLIGHT':
      if (
        flightHasBundledHotel(savedItemData) &&
        isBookableHotel(savedItemData.hotelBundle)
      ) {
        return getHotelRestoreCartURL(invertFlightBundleData(savedItemData))
      } else if (
        flightHasBundledTour(savedItemData) &&
        isBookableTour(savedItemData.tourBundle)
      ) {
        return getTourV1RestoreCartURL(invertFlightBundleData(savedItemData))
      } else {
        return undefined
      }
    case 'CRUISE':
      if (savedItemData.offerType === 'cruise') {
        if (offer) {
          return offerPageURL(offer)
        } else {
          return undefined
        }
      }

      if (savedItemData.offerType === 'tour') {
        if (isBookableTour(savedItemData)) {
          return getTourV1RestoreCartURL(savedItemData)
        } else if (offer) {
          return offerPageURL(offer)
        }
      }
      break
    case 'TOUR':
      if (savedItemData.offerType === 'tour') {
        if (isBookableTour(savedItemData)) {
          return getTourV1RestoreCartURL(savedItemData)
        } else if (offer) {
          return offerPageURL(offer)
        }
      }
      // We don't support tour v2 restore cart yet
      // For now link to offer page
      if (savedItemData.offerType === 'tour_v2' && offer) {
        return offerPageURL(offer)
      }
      break
    case 'EXPERIENCE':
      if (isBookableExperience(savedItemData)) {
        return buildExperienceDeepLinkURL(savedItemData)
      } else if (experience) {
        return experiencePageUrlWithPrefilledState(experience, savedItemData)
      } else {
        return undefined
      }
    default:
      return assertUnreachable(type)
  }
}

export function invertFlightBundleData(
  data: Utils.RequiredProp<SavedFlightSearchData, 'hotelBundle'>,
): Utils.RequiredProp<SavedHotelData, 'flightBundle'>
export function invertFlightBundleData(
  data: Utils.RequiredProp<SavedFlightSearchData, 'tourBundle'>,
): Utils.RequiredProp<SavedTourData, 'flightBundle'>
export function invertFlightBundleData(
  data: SavedFlightSearchData,
):
  | Utils.RequiredProp<SavedHotelData, 'flightBundle'>
  | Utils.RequiredProp<SavedTourData, 'flightBundle'>
  | undefined
export function invertFlightBundleData(
  data: SavedFlightSearchData,
):
  | Utils.RequiredProp<SavedHotelData, 'flightBundle'>
  | Utils.RequiredProp<SavedTourData, 'flightBundle'>
  | undefined {
  const { hotelBundle, tourBundle, ...flightBundle } = data
  if (hotelBundle) {
    return {
      ...hotelBundle,
      flightBundle,
      regionCode: flightBundle.regionCode,
      currencyCode: flightBundle.currencyCode,
    }
  } else if (tourBundle) {
    return {
      ...tourBundle,
      flightBundle,
      regionCode: flightBundle.regionCode,
      currencyCode: flightBundle.currencyCode,
    }
  } else {
    return undefined
  }
}

export function getHotelRestoreCartURL(
  data: SavedHotelData,
  curatedTripId?: string,
) {
  const {
    checkIn,
    checkOut,
    offerType,
    occupancies,
    offerId,
    price,
    currencyCode,
    packageId,
    roomTypeId,
    rateId,
  } = data

  const numberOfNights = moment(checkOut).diff(moment(checkIn), 'days')

  let queryParams: Record<string, string | number | undefined> = {
    productType: getRestoreCartProductType(offerType),
    startDate: checkIn,
    endDate: checkOut,
    numberOfNights,
    offerId,
    adults: occupancies?.map((o) => o.adults).join(','),
    children: occupancies
      ?.map((o) => encodeChildrenOccupancy(o.childrenAge))
      .join(','),
    savedPrice: price,
    savedPriceCurrency: currencyCode,
    curatedTripId,
  }

  if (offerType === OFFER_TYPE_BED_BANK) {
    queryParams.roomId = packageId ?? roomTypeId
    queryParams.rateId = rateId
  } else {
    queryParams.packageId = packageId
    queryParams.roomRateId = rateId
  }

  // Bundled flights
  if (data.flightBundle) {
    queryParams = {
      ...queryParams,
      ...buildBundledFlightQueryParams(data.flightBundle),
    }
  }

  // Some legacy items don't have the necessary data to restore cart to new checkout
  // If they don't, we try the old method

  if (
    data.offerType === 'bedbank_hotel' &&
    CAN_RESTORE_CART_BEDBANK_TO_NEW_CHECKOUT &&
    !data.flightBundle &&
    isBookableHotel(data) &&
    data.occupancies &&
    data.occupancies.length === 1 // Deep linking to new checkout currently doesn't work for bedbank with multiple rooms
  ) {
    return (
      '/checkout-restore-cart?' +
      qs.stringify(
        { items: JSON.stringify([buildCheckoutBedbankItem(data)]) },
        { encode: true },
      )
    )
  }

  if (
    data.offerType !== 'bedbank_hotel' &&
    CAN_RESTORE_CART_LE_HOTELS_TO_NEW_CHECKOUT &&
    !data.flightBundle &&
    isBookableHotel(data)
  ) {
    return (
      '/checkout-restore-cart?' +
      qs.stringify(
        { items: JSON.stringify(buildCheckoutLEAccommodationItems(data)) },
        { encode: true },
      )
    )
  }

  return '/booking-restore-cart?' + qs.stringify(queryParams)
}

export function getTourV1RestoreCartURL(data: SavedTourData) {
  const {
    startDate,
    endDate,
    offerId,
    packageId,
    occupancies,
    price,
    currencyCode,
    offerType,
  } = data

  let queryParams: Record<string, string | number | undefined> = {
    productType: getRestoreCartProductType(offerType),
    startDate,
    endDate,
    offerId,
    packageId,
    adults: occupancies?.map((o) => o.adults).join(','),
    children: occupancies
      ?.map((o) => encodeChildrenOccupancy(o.childrenAge))
      .join(','),
    savedPrice: price,
    savedPriceCurrency: currencyCode,
  }

  // Bundled flights
  if (data.flightBundle) {
    queryParams = {
      ...queryParams,
      ...buildBundledFlightQueryParams(data.flightBundle),
    }
  }

  return '/booking-restore-cart?' + qs.stringify(queryParams)
}

export function buildExperienceDeepLinkURL(savedItemData: SavedExperienceData) {
  return (
    '/checkout-restore-cart?' +
    qs.stringify(
      {
        items: JSON.stringify([buildCheckoutExperienceItem(savedItemData)]),
      },
      { encode: true },
    )
  )
}

function buildBundledFlightQueryParams(
  flightData: NonNullable<SavedHotelData['flightBundle']>,
) {
  const {
    originAirportCode,
    destinationAirportCode,
    departDate,
    returnDate,
    fareClass,
    savedPrice,
  } = flightData

  return {
    originAirportCode,
    destinationAirportCode,
    departDate,
    returnDate,
    fareClass: fareClass.toLowerCase(),
    savedFlightPrice: savedPrice,
  }
}

export function buildStandaloneFlightSearchURL(
  savedItemData: SavedFlightSearchData,
) {
  const {
    originAirportCode,
    originAirportName,
    destinationAirportCode,
    destinationAirportName,
    departDate,
    returnDate,
    fareClass,
    fareType,
    travellerCounts,
  } = savedItemData

  const isOneWayFare = isOneWayFlight(fareType)

  return buildStandaloneFlightSearchUrlFromQueryParams({
    originAirportCode,
    originAirportName,
    destinationAirportCode,
    destinationAirportName,
    departDate,
    returnDate: (!isOneWayFare && returnDate) || null,
    adults: travellerCounts?.adults || DEFAULT_ADULT_OCCUPANTS,
    children: travellerCounts?.children ?? 0,
    infants: travellerCounts?.infants ?? 0,
    childrenAge: travellerCounts?.childrenAge?.join(',') ?? '',
    fareClass: fareClass.toLowerCase(),
    fareType,
  })
}

export const isBookableHotel = ({
  checkIn,
  checkOut,
  roomTypeId,
  rateId,
  packageId,
  occupancies,
  offerType,
}: // regionCode and currencyCode are omitted here because the `hotelBundle` field on flight savedItemData doesn't have them
Omit<SavedHotelData, 'regionCode' | 'currencyCode'>) => {
  if (offerType === 'bedbank_hotel') {
    return (
      !!checkIn &&
      !!checkOut &&
      !!rateId &&
      !!(packageId || roomTypeId) &&
      !!occupancies &&
      occupancies.length > 0
    )
  } else {
    return (
      !!checkIn &&
      !!checkOut &&
      !!roomTypeId &&
      !!packageId &&
      !!rateId &&
      !!occupancies &&
      occupancies.length > 0
    )
  }
}

export const isBookableTour = ({
  startDate,
  endDate,
  packageId,
  occupancies,
}: // regionCode and currencyCode are omitted here because the `tourBundle` field on flight savedItemData doesn't have them
Omit<SavedTourData, 'regionCode' | 'currencyCode'>): boolean => {
  return (
    !!startDate &&
    !!endDate &&
    !!packageId &&
    !!occupancies &&
    occupancies.length > 0
  )
}

export const isBookableExperience = ({
  tickets,
}: SavedExperienceData): boolean => {
  return !!tickets && tickets.length > 0
}

export const isBookableCruise = (data: SavedCruiseData): boolean => {
  if (data.offerType === 'cruise') {
    const {
      offerId,
      cabinCategory,
      cabinCategoryDetails,
      occupancy,
      departure,
    } = data

    return (
      !!offerId &&
      !!cabinCategory &&
      !!cabinCategoryDetails &&
      !!occupancy &&
      !!departure
    )
  } else if (data.offerType === 'tour') {
    return isBookableTour(data)
  }

  throw new Error('Tour V2s are not bookable as cruise')
}

type HotelWithFlightBundle = HotelItem & {
  savedItemData: Utils.RequiredProp<SavedHotelData, 'flightBundle'>
}
type FlightWithHotelBundle = FlightItem & {
  savedItemData: Utils.RequiredProp<SavedFlightSearchData, 'hotelBundle'>
}
type TourWithFlightBundle = TourItem & {
  savedItemData: Utils.RequiredProp<SavedTourData, 'flightBundle'>
}
type FlightWithTourBundle = FlightItem & {
  savedItemData: Utils.RequiredProp<SavedFlightSearchData, 'tourBundle'>
}

export const isBookableHotelFlightBundle = (
  item: LETripItem,
): item is HotelWithFlightBundle | FlightWithHotelBundle => {
  return (
    (item.type === 'HOTEL' &&
      hasBundledFlight(item.savedItemData) &&
      isBookableHotel(item.savedItemData)) ||
    (item.type === 'FLIGHT' &&
      flightHasBundledHotel(item.savedItemData) &&
      isBookableHotel(item.savedItemData.hotelBundle))
  )
}

export const isBookableTourFlightBundle = (
  item: LETripItem,
): item is TourWithFlightBundle | FlightWithTourBundle => {
  return (
    (item.type === 'TOUR' &&
      hasBundledFlight(item.savedItemData) &&
      isBookableTour(item.savedItemData)) ||
    (item.type === 'FLIGHT' &&
      flightHasBundledTour(item.savedItemData) &&
      isBookableTour(item.savedItemData.tourBundle))
  )
}

export const isBookableItem = (item: LETripItem) => {
  switch (item.type) {
    case 'CRUISE': {
      return isBookableCruise(item.savedItemData)
    }
    case 'EXPERIENCE': {
      return isBookableExperience(item.savedItemData)
    }
    case 'FLIGHT': {
      return false
    }
    case 'TOUR': {
      return isBookableTour(item.savedItemData)
    }
    case 'HOTEL': {
      return isBookableHotel(item.savedItemData)
    }
  }
}

export const buildProductLinkWithQueryParams = (
  item: LETripItem,
  withPrefilledState: boolean = true,
  {
    offer,
    experience,
  }: {
    offer?: OfferTypeForTripItemTypes
    experience?: App.ExperienceOffer
  } = {},
  params: { [key: string]: any },
): string | undefined => {
  const url = buildProductLink(item, withPrefilledState, { offer, experience })
  return url && addQueryParamsToPath(url, params)
}

export const buildProductLink = (
  item: LETripItem,
  withPrefilledState: boolean = true,
  {
    offer,
    experience,
  }: {
    offer?: OfferTypeForTripItemTypes
    experience?: App.ExperienceOffer
  } = {},
): string | undefined => {
  switch (item.type) {
    case 'CRUISE':
      if (item.savedItemData.offerType === 'cruise') {
        return getCruiseV2OfferUrl(item)
      }
      if (item.savedItemData.offerType === 'tour') {
        if (offer) {
          return withPrefilledState
            ? offerPageUrlWithPrefilledState(offer, item)
            : offerPageURL(offer)
        }
      }
      break
    case 'EXPERIENCE':
      if (experience) {
        return withPrefilledState
          ? experiencePageUrlWithPrefilledState(
              experience,
              item.savedItemData,
              false,
            )
          : getExperienceOfferPageURL(experience)
      }
      break
    case 'FLIGHT':
      return buildStandaloneFlightSearchURL(item.savedItemData)
    case 'TOUR':
    case 'HOTEL':
      if (offer) {
        return withPrefilledState
          ? offerPageUrlWithPrefilledState(offer, item)
          : offerPageURL(offer)
      }
  }
}

function buildCheckoutLEAccommodationItems({
  offerId,
  checkIn,
  checkOut,
  occupancies,
  packageId,
  rateId,
  roomTypeId,
  flightBundle,
}: SavedHotelData): Array<ValidHotelInstantBookingCheckoutSchema> {
  if (
    !(
      offerId &&
      checkIn &&
      checkOut &&
      occupancies &&
      occupancies?.length > 0 &&
      packageId &&
      roomTypeId &&
      rateId
    )
  ) {
    throw new Error('Cant restore cart, not enough info')
  }

  return occupancies.map((occupants) => ({
    itemType: 'hotel',
    reservationType: 'instant_booking',
    offerId,
    checkIn,
    checkOut,
    occupancy: {
      roomId: roomTypeId,
      children: occupants.children,
      adults: occupants.adults,
      childrenAge: occupants.childrenAge,
      infants: occupants.infants,
    },
    duration: moment(checkOut).diff(moment(checkIn), 'days'),
    packageId,
    roomRateId: rateId,
    isBundled: !!flightBundle,
    flightBasePrice: flightBundle?.savedPrice,
  }))
}

function buildCheckoutExperienceItem({
  experienceId,
  date,
  time,
  leExclusive,
  redemptionLocationId,
  pickupPointId,
  languageId,
  tickets,
}: SavedExperienceData): ValidExperienceCheckoutSchema {
  if (!(tickets && tickets.length > 0)) {
    throw new Error('Cant restore cart, not enough info')
  }

  return {
    itemType: 'experience',
    experienceId,
    date: date ?? moment().format(ISO_DATE_FORMAT),
    time,
    leExclusive: leExclusive ?? false,
    languageId,
    redemptionLocationId,
    pickupPointId,
    offerType: leExclusive ? 'flash' : 'channel_manager',
    tickets: tickets.map((t) => ({
      count: t.count,
      fareType: t.fareType,
      name: t.name,
      ticketId: t.ticketId,
      unavailable: false,
    })),
    addon: undefined,
    flightBasePrice: undefined,
    isBundled: undefined,
    isBuyNowBookLater: undefined,
    isGift: undefined,
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function buildCheckoutBedbankItem({
  offerId,
  checkIn,
  checkOut,
  occupancies,
  packageId,
  rateId,
  roomTypeId,
  flightBundle,
}: SavedHotelData): ValidBedbankCheckoutSchema {
  if (
    !(
      offerId &&
      checkIn &&
      checkOut &&
      occupancies &&
      occupancies?.length > 0 &&
      (roomTypeId || packageId) &&
      rateId
    )
  ) {
    throw new Error('Cant restore cart, not enough info')
  }

  const occupants = occupancies[0]

  return {
    itemType: 'bedbankHotel',
    offerId,
    checkIn,
    checkOut,
    occupancy: {
      roomId: roomTypeId,
      children: occupants.children,
      adults: occupants.adults,
      childrenAge: occupants.childrenAge,
      infants: occupants.infants,
    },
    roomId: roomTypeId ?? packageId!,
    roomRateId: rateId,
    duration: moment(checkOut).diff(moment(checkIn), 'days'),
    isBundled: !!flightBundle,
    flightBasePrice: flightBundle?.adultPrice,
  }
}
